import React, { useState } from "react";
import { Button, Typography, Box } from "@mui/material";
import { FileUpload, ErrorOutline } from "@mui/icons-material";

interface AddFileButtonProps {
  maxFileSizeMB: number;
  allowedExtensions: string[];
  onFileUploaded: (file: File) => void;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
  children?: React.ReactNode;
}

export const AddFileButton: React.FC<AddFileButtonProps> = ({
  maxFileSizeMB,
  allowedExtensions,
  onFileUploaded,
  disabled = false,
  size = "medium",
  variant = "outlined",
  children = "Add From File",
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validateFile = (file: File) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const fileSizeMB = file.size / (1024 * 1024);

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setError(
        `Tipo de archivo inválido. Tipos permitidos: ${allowedExtensions.join(", ")}`,
      );
      return false;
    }

    if (fileSizeMB > maxFileSizeMB) {
      setError(`El archivo excede el límite de ${maxFileSizeMB} MB.`);
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setLoading(true);
        onFileUploaded(file);
        setLoading(false);
      }
    }
    // Reset input value para permitir seleccionar el mismo archivo nuevamente
    e.target.value = "";
  };

  return (
    <Box>
      <Button
        component="label"
        variant={variant}
        size={size}
        startIcon={<FileUpload />}
        disabled={disabled || loading}
      >
        {children}
        <input
          type="file"
          hidden
          onChange={handleFileSelect}
          accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
        />
      </Button>

      {error && (
        <Box mt={1}>
          <Typography
            color="error"
            variant="caption"
            display="flex"
            alignItems="center"
          >
            <ErrorOutline fontSize="small" sx={{ mr: 0.5 }} /> {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
